import axios from 'axios'
import i18next from 'i18next'
import { API_URL } from 'services/config'
import { multiquoteIdFromApiResponse, sendMultiquoteCreateEvent } from 'analytics'
import VehicleBrand from 'models/VehicleBrand'
import VehicleGnc from 'models/VehicleGnc'
import VehicleGroup from 'models/VehicleGroup'
import VehicleVersion from 'models/VehicleVersion'
import CarCoverageSummary from 'models/CarCoverageSummary'
import VehicleCluster from 'models/VehicleCluster'
import ClusterCustomer from 'models/ClusterCustomer'
import AdvancedConfigOption from 'models/AdvancedConfigOption'
import SelectOption from 'models/SelectOption'
import Province from 'models/Province'
import MultiquoteDebugData from 'models/MultiquoteDebug/MultiquoteDebugData'
import { RISK_TYPE_CAR } from 'util/riskTypes'
import mapGroupedCoveragesToClusterArray from 'util/mapGroupedCoveragesToClusterArray'
import RequiringCoveragesService from './RequiringCoveragesService'

export const QUOTE_CAR_FORM_NAME = 'carQuoteData'
export const CBU = 'bankAccount'
export const CARD = 'creditCard'

class QuoteCreationService {
    companiesJson = [
        { name: 'Zurich', id: 'Zurich' },
        { name: 'Allianz', id: 'Allianz' },
        { name: 'Sura', id: 'Sura' },
    ]

    genderJson = [
        { name: i18next.t('quoteCreation:female'), id: 'female' },
        { name: i18next.t('quoteCreation:male'), id: 'male' },
    ]

    getYears = (type = RISK_TYPE_CAR) =>
        axios
            .get(`${API_URL}/multi-quoter/${type}/years`)
            .then(({ data }) => (Array.isArray(data?.data) ? data.data : data?.data ?? data))

    getExtendedYears = (type = RISK_TYPE_CAR) =>
        axios
            .get(`${API_URL}/multi-quoter/${type}/years?extended=true`)
            .then(({ data }) => (Array.isArray(data?.data) ? data.data : data?.data ?? data))

    getBrands = (type, year) =>
        axios
            .get(`${API_URL}/multi-quoter/${type}/brands?year=${year}`)
            .then((response) => response.data.data.map((brand) => VehicleBrand.fromJson(brand)))

    getGroups = (type, year, brand) =>
        axios
            .get(`${API_URL}/multi-quoter/${type}/brands/${brand.id}/groups?year=${year}`)
            .then((response) => response.data.data.map((group) => VehicleGroup.fromJson(group)))

    getVersions = (type, year, brand) =>
        axios
            .get(`${API_URL}/multi-quoter/${type}/models?year=${year}&brand=${brand?.id}`)
            .then(({ data: { data } }) =>
                data.map((carVersion) => VehicleVersion.fromJson(carVersion))
            )

    getMoreVersions = (type, year, brand, group) =>
        axios
            .post(`${API_URL}/multi-quoter/${type}/models/search-more`, {
                year,
                brand: Number(brand?.id),
                group: Number(group?.id),
            })
            .then(({ data: { data } }) =>
                data.map((carVersion) => VehicleVersion.fromJson(carVersion))
            )

    getCoverageSummary = (type, year, brand, carVersion) =>
        axios
            .get(`${API_URL}/multi-quoter/${type}/summary?year=${year}&brand=${brand}&model=${carVersion}`)
            .then((response) => CarCoverageSummary.fromJson(response.data))

    getGncStatus = (year) =>
        axios
            .get(`${API_URL}/gnc?year=${year}`)
            .then(({ data }) => VehicleGnc.fromJson(data.data))

    buildQuote = (quoteParams, multiQuoteType = RISK_TYPE_CAR) =>
        axios.post(`${API_URL}/multi-quotes/${multiQuoteType}`, quoteParams).then(({ data }) => {
            const {
                data: { id: multiquoterId },
            } = data
            sendMultiquoteCreateEvent({
                action: 'create',
                multiquoteType: multiQuoteType,
                multiquoteId: multiquoterId,
            })
            return multiquoterId
        })

    static async requiringCoverages(id, onMessage) {
        await RequiringCoveragesService.requireCoverages(id, (response) => {
            onMessage(response)
        })
    }

    getMultiQuote = (id, { full = false } = {}) => {
        const params = full ? { full: true } : {}

        return axios.get(`${API_URL}/multi-quotes/${id}`, { params }).then(({ data }) => data)
    }

    showMultiquote = (id) =>
        this.getMultiQuote(id).then((data) => {
            const { multiQuoteRequestStatus } = data
            if (multiQuoteRequestStatus === 'running') {
                return { multiQuoteRequestStatus }
            }
            const clusterArray = mapGroupedCoveragesToClusterArray(data.coverages)
            const clusterCustomer = ClusterCustomer.fromJson(data.data.customer)

            return { clusterArray, clusterCustomer, multiQuoteRequestStatus }
        })

    getMultiquoteDebug = (multiquoteId) =>
        axios
            .get(`${API_URL}/multi-quotes/debug/${multiquoteId}`)
            .then((response) => MultiquoteDebugData.fromApiResponse(response))

    sendCoveragesEmail = (coveragesEmailData) =>
        axios
            .post(`${API_URL}/multi-quoter/send-email`, coveragesEmailData)
            .then((response) => response)

    getCompanies = () =>
        new Promise((resolve) => {
            resolve(this.companiesJson.map((company) => AdvancedConfigOption.fromJson(company)))
        })

    getLocalities = (postalCode) =>
        axios.get(`${API_URL}/locations?postcode=${postalCode}`).then(({ data }) => {
            const provinces = []
            data.data.map((locality) => {
                const findIndex = provinces.findIndex((province) => {
                    return province.value === locality.province.name
                })
                const localityId = locality.id
                const localityName = locality.name
                const provinceId = locality.province.id
                const provinceName = locality.province.name
                if (provinces.length < 1 || findIndex === -1) {
                    provinces.push(
                        Province.fromJson(provinceId, provinceName, [
                            SelectOption.fromJson({ id: localityId, name: localityName }),
                        ])
                    )
                } else {
                    provinces[findIndex].addLocality(
                        SelectOption.fromJson({ id: localityId, name: localityName })
                    )
                }
                return true
            })
            return provinces
        })

    getGenderOptions = () =>
        new Promise((resolve) => {
            resolve(this.genderJson.map((gender) => gender))
        })

    getInsuranceCompanies = (multiQuoteType) => {
        const url = multiQuoteType
            ? `${API_URL}/multi-quoter/insurance-companies/${multiQuoteType}`
            : `${API_URL}/multi-quoter/insurance-companies`

        return axios
            .get(url)
            .then(({ data }) => data.data.map((insuranceCompany) => insuranceCompany))
    }

    getClusters = async (multiquoteId) => {
        return axios
            .get(`${API_URL}/multi-quoter/clusters/${multiquoteId}`)
            .then(({ data: { data } }) =>
                data.map((cluster) => new VehicleCluster(cluster.code, cluster.name))
            )
    }

    createQuoteCustomer = (quoteCustomerData) =>
        axios.post(`${API_URL}/quote/customer`, quoteCustomerData).then((response) => response)

    downloadPdf = (params) =>
        axios
            .get(`${API_URL}/multi-quoter/download`, {
                params,
            })
            .then((response) => {
                const pdf = response.data.data
                const linkSource = `data:application/pdf;base64,${pdf.content}`
                const downloadLink = document.createElement('a')
                downloadLink.href = linkSource
                downloadLink.download = pdf.filename
                downloadLink.click()
            })

    cloneQuote = async (multiQuoteId, localityId, commissionType) => {
        const params = {}
        if (localityId) params.locality = localityId
        if (commissionType) params.commissionType = commissionType

        const response = await axios.post(`${API_URL}/multi-quotes/${multiQuoteId}/clone`, params)
        sendMultiquoteCreateEvent({
            action: 'clone',
            sourceMultiquoteId: multiQuoteId,
            multiquoteId: multiquoteIdFromApiResponse(response),
            localityId,
            commissionType,
        })
        return response.data
    }

    // TODO: eliminar este método y reemplazar las llamadas por "cloneQuote"
    reQuote = async (multiQuoteId, localityId, commissionType) => {
        const params = {}
        if (localityId) params.locality = localityId
        if (commissionType) params.commissionType = commissionType

        const response = await axios.post(`${API_URL}/multi-quotes/${multiQuoteId}/clone`, params)
        sendMultiquoteCreateEvent({
            action: 'requote',
            sourceMultiquoteId: multiQuoteId,
            multiquoteId: multiquoteIdFromApiResponse(response),
            localityId,
            commissionType,
        })
        return response.data
    }

    fromStorage() {
        return this
    }
}

export default QuoteCreationService
